import {
  findingsFrom,
  localFindings,
  outlineFor,
} from '../../managers/document_check_run';
import {
  createDecisions,
  OUTCOMES,
} from '../../managers/document_check_decisions';
import {
  clearAmbient,
  clearHighlights,
  findingAtPoint,
  flashHighlights,
  paintAmbient,
  paintHighlights,
  repaintAmbient,
  supportsPreciseHighlights,
} from '../../managers/document_highlight';

/**
 * lp-015. The checks panel.
 *
 * Findings mark their place in the lease AND list here, which reads as a
 * contradiction of PRODUCT.md's second principle until you notice what that
 * principle actually forbids: a panel list DUPLICATING a set the document
 * already presents as reviewable. Counterparty changes are such a set —
 * sequential, with Next and Previous, a queue you work through. Findings have no
 * traversal at all, and a drafter cannot scroll sixty to a hundred and twenty
 * pages hunting for the eleven places we marked. The list is not a second
 * inventory; it is the only one.
 *
 * So the two carry different jobs and neither substitutes for the other: the
 * list answers "have I dealt with all of them", which is the question before
 * sending, and the marks answer "what is this one about", which is the question
 * once you are looking at it.
 */
angular.module('LeasePilot').directive('documentChecks', [
  '$rootScope',
  '$timeout',
  'ApiService',
  function($rootScope, $timeout, ApiService) {
    return {
      restrict: 'E',
      templateUrl: 'document_checks.html',
      link: function(scope) {
        // Read when needed rather than captured here. This directive used to
        // link only when somebody opened the panel, by which time the lease had
        // long since loaded; it now links with the editor, which is before
        // `$rootScope.lease` exists. Captured at link time the type is blank —
        // which the endpoint rightly rejects — and the decisions store keys
        // itself to "unsaved" and keeps every decision under the wrong lease.
        function currentLease() {
          return $rootScope.lease || {};
        }

        let store = null;
        let storeFor = null;

        // lp-016 §5. The store talks to the server, and the transport is built
        // here because it is the only place that knows which lease this is.
        //
        // A lease with no id yet has no transport, so the panel still works on
        // a document that has never been saved — the decisions are that
        // browser's alone until there is something to attach them to, which is
        // the honest behaviour rather than an error message about saving first.
        function transportFor(id) {
          if (!id || id === 'unsaved') return null;

          return {
            list: function() {
              return ApiService.listCheckDecisions(id);
            },
            record: function(decision) {
              return ApiService.recordCheckDecision(id, decision);
            },
            clear: function(findingId) {
              return ApiService.clearCheckDecision(id, findingId);
            },
          };
        }

        function decisions() {
          const id = currentLease().id || 'unsaved';
          if (!store || storeFor !== id) {
            const fresh = createDecisions(id, transportFor(id));
            store = fresh;
            storeFor = id;
            // Everyone's set-asides, not just this browser's. Regrouping when
            // it lands is what stops the panel briefly asking about thirty
            // findings a colleague already answered. Guarded on identity
            // because a lease can change under a slow request, and a late
            // answer must not regroup the panel of a different document.
            fresh.load().then(function() {
              if (store === fresh) regroup();
            });
          }
          return store;
        }

        /**
         * A finding, named without quoting the document or the playbook.
         *
         * A finding id is `check:scope:evidence`, and the evidence is prose —
         * a checklist finding's id carries the reader's whole statement. The
         * first two segments identify WHICH finding without carrying any of it:
         * `playbook_ladder:holdover`, `integrity_blank_run:p3600`.
         *
         * This matters because audit events do not stay in the tenant.
         * `events_controller` forwards the AUDIT_LOG channel to Tinybird with
         * the whole payload, and this event used to carry `finding.summary` —
         * which for a playbook finding reads "Holdover at 110 percent of prior
         * year's Rent is past your minimum of 125". That is the firm's declared
         * floor, the most commercially sensitive number in the product, leaving
         * the tenant in an analytics blob. `comments_directive.js` states the
         * rule this broke: the text belongs in the company-scoped ledger, and
         * the event says only what it needs to about the decision.
         */
        function findingRef(id) {
          return String(id || '').split(':').slice(0, 2).join(':');
        }

        // lp-013 §4's guard, for the same reason it exists there: the editor
        // runs in tests and in contexts where `window.track` is absent, and an
        // audit call must never be why a drafter cannot record a decision.
        function audit(EventClass, data) {
          try {
            if (window.track && typeof EventClass === 'function') {
              window.track.event(new EventClass(data));
            }
          } catch (error) {
            // Deliberately swallowed. See above.
          }
        }

        /**
         * Whether this document is worth checking yet.
         *
         * A lease created from a form is a projection of that form: full of
         * blanks because the deal terms have not been entered, and every one of
         * those blanks is a field waiting rather than a defect. The mechanical
         * families are right to find them and the panel is wrong to report
         * them — "forty findings" on a document nobody has drafted is true,
         * useless, and the fastest way to teach somebody that this panel is
         * noise.
         *
         * So the check arms itself the first time the document is SENT, which
         * is the moment its blanks stop being fields and start being defects.
         * `firstSentAt` comes off the lease and is derived from the event log,
         * so a document downloaded at any point in its life is already armed.
         *
         * Two ways to arm early, both deliberate. Opening the panel is somebody
         * asking, and a question deserves an answer whatever the document's
         * state. And a send arms it in the same gesture that makes it matter.
         */
        // Which lease was armed, not merely "something was". A drafter who
        // opens the panel on one lease and then creates a new one from a form
        // would otherwise carry the arming across and get forty blanks on a
        // document nobody has drafted — the exact failure arming exists to
        // prevent.
        let armedFor = null;

        function readyToReport() {
          const id = currentLease().id || 'unsaved';
          if (armedFor === id) return true;

          // `firstSentAt`, camelCase, because every API response passes through
          // the `toCamel` interceptor in `lease_pilot_app.js` before anything
          // here sees it. The snake_case form is read too: it is what the model
          // and the serializer call the field, it is what somebody grepping for
          // it will find, and this reader has already been wrong about that
          // once — it shipped looking only for `first_sent_at`, which no
          // response has ever contained, so the checks never armed at all.
          const lease = currentLease();
          return Boolean(lease.firstSentAt || lease.first_sent_at);
        }

        function arm() {
          armedFor = currentLease().id || 'unsaved';
        }

        scope.state = 'idle';
        scope.findings = [];
        scope.counts = { total: 0, open: 0, fixed: 0, aside: 0 };
        scope.groups = [];
        scope.selected = null;
        scope.unavailable = null;
        scope.readUpTo = null;
        scope.didNotRun = [];
        scope.error = null;
        scope.lastRunAt = null;
        scope.reading = false;
        scope.stale = false;
        // Which severities are folded away. Keyed by severity rather than by
        // index so a group keeps its state when a re-run changes the counts.
        scope.collapsed = {};

        const SEVERITIES = ['high', 'medium', 'low'];
        const LABELS = { high: 'High', medium: 'Medium', low: 'Low' };

        function regroup() {
          // Nothing to say about a document that has never been sent, until
          // somebody asks. The findings still exist; they are simply not news.
          if (!readyToReport()) {
            scope.counts = { total: 0, open: 0, fixed: 0, aside: 0 };
            scope.groups = [];
            markDocument([]);
            publish();
            return;
          }

          const applied = decisions().apply(scope.findings);
          scope.counts = applied.counts;
          scope.groups = SEVERITIES.map(severity => ({
            severity,
            label: LABELS[severity],
            findings: applied.findings.filter(f => f.severity === severity),
          })).filter(group => group.findings.length);
          markDocument(applied.findings);
          publish();
        }

        // What the status bar renders. It sits outside this element — it has to,
        // because its whole job is to be readable when the panel is closed — so
        // the summary goes on `$rootScope` rather than on our scope.
        //
        // A count and the worst thing in it, which is the pair a drafter needs
        // to decide whether to open this at all: how much is here, and is any of
        // it the kind that stops a lease going out.
        function publish() {
          const openIn = severity => {
            const group = scope.groups.find(g => g.severity === severity);
            if (!group) return 0;
            return group.findings.filter(f => !f.outcome).length;
          };

          const high = openIn('high');
          const medium = openIn('medium');

          $rootScope.checks = {
            // So the status bar can say why it is quiet. "No findings" on a
            // document nobody has drafted claims it is clean, which is a
            // stronger thing than we have any right to say.
            armed: readyToReport(),
            state: scope.state,
            reading: scope.reading,
            stale: scope.stale,
            total: scope.counts.open,
            high: high,
            worst: high ? 'high' : medium ? 'medium' : 'low',
          };
        }

        /**
         * The marks in the lease itself.
         *
         * Cleared and rewritten on every run rather than diffed: a run produces
         * the whole truth, and reconciling two sets of marks would be a second
         * place for them to disagree with the list.
         */
        // Whether the drafter is looking at the checks at all.
        //
        // The marks belong to this panel. Left on while somebody works in Deal
        // Terms they are an unexplained rash of underlines on a document that
        // is not being checked at the time, with nothing on screen to say what
        // they mean — and the panel is where the sentence explaining each one
        // lives. They come back the moment the panel does.
        function panelOpen() {
          return scope.leftPane === 'checks';
        }

        function markDocument(findings) {
          const root = document.querySelector('.lease.lease--original');
          if (!root) return;

          root
            .querySelectorAll('[data-check-severity]')
            .forEach(el => el.removeAttribute('data-check-severity'));
          clearAmbient();

          if (!panelOpen()) return;

          // The words, not the paragraph containing them.
          //
          // A finding is about text. An outline round the block it sits in says
          // "somewhere in here", and on a six-line clause with one bad figure
          // that is five lines of misdirection. Every finding's own hits are
          // painted at once, in one faint layer, and selecting one adds
          // emphasis on top rather than replacing the map.
          const painted = paintAmbient(
            root,
            findings.reduce(
              (all, finding) =>
                all.concat(
                  (finding.hits || []).map(hit => ({
                    ...hit,
                    // Carried so a click on the underline can find its way back
                    // to the finding that drew it.
                    findingId: finding.id,
                  })),
                ),
              [],
            ),
          );
          root.classList.toggle('lease--precise-checks', painted > 0);
          if (painted > 0) watchDocument();

          // Only where the browser cannot paint ranges. There, a block mark is
          // the whole of what we can say, and saying it coarsely beats saying
          // nothing.
          if (supportsPreciseHighlights()) return;

          findings.forEach(finding => {
            const pid = finding.at && finding.at.pid;
            if (!pid || finding.outcome) return;

            const block = root.querySelector(`[pid="${pid}"]`);
            if (block)
              block.setAttribute('data-check-severity', finding.severity);
          });
        }

        // Two costs, so two runs. The mechanical families are a walk of the
        // DOM and nothing else, so they re-run every time the document changes.
        // Reading the figures is a model call over a hundred-odd pages, so it
        // runs when you open the panel and when you ask for it again — never on
        // a keystroke, and never for a drafter who has not opened the panel.
        let mechanical = [];
        let read = [];

        function compose() {
          scope.findings = mechanical.concat(read);
          regroup();
        }

        // Returns the run, so a caller can wait for it. A fire-and-forget
        // version passed its tests on microtask ordering rather than on the
        // behaviour being tested, which is the sort of green that goes red the
        // first time something upstream gets slower.
        scope.run = function(deep) {
          const sections = outlineFor(document);
          if (!sections.length) {
            // A lease mid-render has no outline. That is a reason to run later,
            // not a failure to report to somebody who asked for a check.
            if (scope.state === 'idle') return Promise.resolve();
          } else {
            mechanical = localFindings(sections);
            compose();
            if (scope.state === 'idle') scope.state = 'ready';
            // The figures were read against a document that has since changed.
            // Said rather than silently re-read: the re-read is the expensive
            // half, and whether it is worth paying for is the drafter's call.
            if (!deep && read.length) {
              scope.stale = true;
              publish();
            }
          }

          if (!deep || scope.reading || !sections.length)
            return Promise.resolve();

          scope.reading = true;
          scope.stale = false;
          scope.error = null;
          publish();

          // Which document this read is about. A model pass over a hundred and
          // twenty pages takes a minute or two, and a drafter can open another
          // lease in that time — `decisions()` already guards its own late
          // answer this way and this one did not, so lease B could be shown
          // lease A's findings, painted onto B's paragraphs by pid.
          const askedAbout = currentLease().id || 'unsaved';

          return ApiService.checkDocument({
            documentId: currentLease().id,
            documentType: currentLease().type,
            sections: sections,
          })
            .then(function(response) {
              if ((currentLease().id || 'unsaved') !== askedAbout) return;

              const result = findingsFrom(response);
              read = result.findings;
              // Only about the ladder read. The mechanical findings above ran
              // whatever the playbook does or does not say.
              scope.unavailable = result.unavailable;
              // Null on every document the reading pass got through, which is
              // most of them. Said only when it is true.
              scope.readUpTo = result.readUpTo || null;
              // Which families fell over. Empty on an ordinary run, which is
              // most of them — this is said only when it is true, for the same
              // reason the coverage line is.
              scope.didNotRun = result.didNotRun || [];
              compose();
              scope.lastRunAt = new Date();
              // "Unavailable" means we had nothing to compare against, which is
              // only true when nothing came back at all. A playbook with no
              // ladders no longer silences the families that never needed one.
              scope.state =
                result.unavailable && !mechanical.length && !read.length
                  ? 'unavailable'
                  : 'ready';
            })
            .catch(function() {
              if ((currentLease().id || 'unsaved') !== askedAbout) return;
              // The mechanical findings stand. Only the figures went unread.
              scope.state = 'ready';
              scope.error =
                'The figures could not be checked against your playbook. Everything else below still ran.';
            })
            .finally(function() {
              if ((currentLease().id || 'unsaved') !== askedAbout) return;
              scope.reading = false;
              publish();
            });
        };

        // The families say what kind of check spoke, in the drafter's terms
        // rather than ours: one reads the playbook, the other reads the
        // document on its own and needs no playbook at all.
        const FAMILY_LABELS = {
          playbook: 'Your playbook',
          integrity: 'The document itself',
          checklist: 'Your checklist',
        };

        scope.familyOf = function(finding) {
          return (finding && FAMILY_LABELS[finding.family]) || 'Checks';
        };

        scope.toggleGroup = function(group) {
          scope.collapsed[group.severity] = !scope.collapsed[group.severity];
        };

        // The block a finding points at, while that finding is the one open.
        // Distinct from the ambient mark: the mark says "something here", and
        // this says "this is the one you are reading about".
        function markSelected(finding) {
          const root = document.querySelector('.lease.lease--original');
          if (!root) return;

          root
            .querySelectorAll('[data-check-selected]')
            .forEach(el => el.removeAttribute('data-check-selected'));
          clearHighlights();

          if (!finding) return;

          // The words themselves where the browser can paint ranges, and the
          // paragraph where it cannot. The class tells the stylesheet which of
          // the two happened, so the paragraph fill stands down when something
          // more exact has already been said.
          const lit = paintHighlights(root, finding.hits);
          root.classList.toggle(
            'lease--precise-checks',
            supportsPreciseHighlights() && lit > 0,
          );

          const pid = finding.at && finding.at.pid;
          if (!pid) return;

          const block = root.querySelector(`[pid="${pid}"]`);
          if (block) block.setAttribute('data-check-selected', finding.severity);
        }

        scope.select = function(finding) {
          // lp-016 §6. Where the list was when you left it.
          //
          // The list is removed from the DOM while a finding is open, so its
          // scroll position goes with it. On a lease with thirty findings that
          // meant every trip into a finding dropped you back at the top, and
          // the row you were working through was somewhere below the fold.
          rememberListScroll();
          scope.selected = finding;
          markSelected(finding);
          scope.locate(finding);
        };

        // lp-016 §6. Traversal.
        //
        // Thirty findings meant thirty round-trips to the list, one mouse
        // journey each way. Counterparty review next door has had Previous and
        // Next since the beginning; there was never a reason for this panel to
        // be the one surface where you work through a queue one item at a time
        // by hand.
        //
        // Over the flat list in the order the panel shows it — severity groups
        // in order, findings within them — including ones already set aside.
        // Skipping those would make Next behave differently depending on
        // decisions you cannot see from inside a finding, and would leave no
        // way to step back into one you had just set aside by mistake.
        //
        // No global keyboard shortcut. These are real buttons in tab order,
        // which is the keyboard reach that matters; binding a key would take a
        // keystroke away from a drafter typing into a hundred-page lease.
        function ordered() {
          return scope.groups.reduce(
            (all, group) => all.concat(group.findings),
            [],
          );
        }

        function positionOf(finding) {
          if (!finding) return -1;
          return ordered().findIndex(f => f.id === finding.id);
        }

        // One-based, for a person. Zero when nothing is open.
        scope.position = function() {
          return positionOf(scope.selected) + 1;
        };

        scope.findingCount = function() {
          return ordered().length;
        };

        scope.canStep = function(delta) {
          const next = positionOf(scope.selected) + delta;
          return next >= 0 && next < ordered().length;
        };

        scope.step = function(delta) {
          const all = ordered();
          const next = positionOf(scope.selected) + delta;
          if (next < 0 || next >= all.length) return;

          scope.selected = all[next];
          markSelected(scope.selected);
          scope.locate(scope.selected);
        };

        // Taking you back. Opening a finding scrolls you to it once, and then
        // you read, edit, and scroll away — after which the only route back was
        // to leave the finding and open it again.
        scope.locate = function(finding) {
          const pid = finding && finding.at && finding.at.pid;
          if (!pid) return;

          const block = document.querySelector(
            `.lease.lease--original [pid="${pid}"]`,
          );
          if (block)
            block.scrollIntoView({ behavior: 'smooth', block: 'center' });

          // The scroll puts it in front of you; the pulse is what makes you see
          // it, on a page where the mark may have been in view all along.
          flashHighlights();
        };

        /**
         * Take me to where this term is defined.
         *
         * A drafter reading "the uncapitalised form does not carry the
         * definition" has one obvious next question — what does the definition
         * actually say — and in a hundred and twenty pages that is a search
         * rather than a glance.
         *
         * The definition is lit as well as scrolled to, using the same selected
         * layer the finding itself uses. That is deliberate: you asked to be
         * shown the definition, so the definition is what should be emphasised
         * while you read it. Selecting anything else repaints, and Show me puts
         * the finding's own words back.
         */
        scope.locateDefinition = function(finding) {
          const where = finding && finding.definedAt;
          if (!where || !where.pid) return;

          const root = leaseRoot();
          if (root && where.text)
            paintHighlights(root, [{ pid: where.pid, text: where.text }]);

          const block = document.querySelector(
            `.lease.lease--original [pid="${where.pid}"]`,
          );
          if (block)
            block.scrollIntoView({ behavior: 'smooth', block: 'center' });

          flashHighlights();
        };

        scope.backToList = function() {
          scope.selected = null;
          markSelected(null);
          restoreListScroll();
        };

        // The panel's own scroller, held across the trip into a finding. Read
        // from the DOM rather than tracked, because the list is rebuilt by
        // `regroup()` on every run and any offset we cached would be measured
        // against a list that no longer exists.
        let listScroll = 0;

        function listScroller() {
          return document.querySelector('.checks .checks__body');
        }

        function rememberListScroll() {
          const scroller = listScroller();
          if (scroller) listScroll = scroller.scrollTop;
        }

        function restoreListScroll() {
          if (!listScroll) return;
          // After the digest that puts the list back. Setting scrollTop on an
          // element that is not in the DOM yet does nothing at all.
          $timeout(function() {
            const scroller = listScroller();
            if (scroller) scroller.scrollTop = listScroll;
          });
        }

        scope.setAside = function(finding) {
          decisions().record(finding.id, OUTCOMES.ASIDE, new Date().toISOString(), {
            // What the finding said, sent with the decision because findings do
            // not persist. Without it, the record of somebody knowingly
            // overriding their own floor is an opaque identifier.
            family: finding.family,
            severity: finding.severity,
            summary: finding.summary,
            sectionNumber: finding.at && finding.at.number,
          });

          audit(window.CheckFindingSetAsideEvent, {
            documentId: currentLease().id,
            finding: findingRef(finding.id),
            family: scope.familyOf(finding),
            severity: finding.severity,
            sectionNumber: (finding.at && finding.at.number) || null,
          });

          scope.selected = null;
          regroup();
        };

        scope.reopen = function(finding) {
          decisions().clear(finding.id);

          audit(window.CheckFindingReopenedEvent, {
            documentId: currentLease().id,
            finding: findingRef(finding.id),
            family: scope.familyOf(finding),
            severity: finding.severity,
            sectionNumber: (finding.at && finding.at.number) || null,
          });

          regroup();
        };

        scope.addressed = function() {
          return scope.counts.fixed + scope.counts.aside;
        };

        scope.percentAddressed = function() {
          if (!scope.counts.total) return 0;
          return Math.round((scope.addressed() / scope.counts.total) * 100);
        };

        // Structure changes are when a check can be wrong without anybody
        // touching the panel, so that is when it re-runs. Not on keystroke: a
        // reader over a hundred-page lease is not something to fire while
        // somebody is typing into it.
        const stopWatching = $rootScope.$on(
          'lp:document-structure-changed',
          function() {
            scope.run(false);
          },
        );

        /**
         * Keep the marks alive while the document is being edited.
         *
         * A `Range` holds a text node and an offset into it, and the editor
         * splits text nodes constantly — placing the caret makes it inspect
         * which decorations apply there, and inspecting means splitting. A
         * range painted a moment ago can end up pointing at a node that is no
         * longer in the document, or at half of one, and the underline vanishes
         * or survives in pieces.
         *
         * Rebuilding is the answer rather than a workaround, because these
         * ranges were always derived from `{pid, text, occurrence}`. What was
         * missing was anything watching for the document to move.
         *
         * Debounced, and only while the panel is open. Typing produces a
         * mutation per keystroke and rebuilding sixty ranges on each one would
         * be felt; a beat after the typing stops, nobody notices. Nothing here
         * touches the document, so a repaint cannot disturb the caret.
         */
        const REPAINT_AFTER = 200;
        let repaintTimer = null;

        function scheduleRepaint() {
          if (!panelOpen() || !supportsPreciseHighlights()) return;

          $timeout.cancel(repaintTimer);
          repaintTimer = $timeout(function() {
            repaintAmbient();
            if (scope.selected) paintHighlights(leaseRoot(), scope.selected.hits);
          }, REPAINT_AFTER, false);
        }

        function leaseRoot() {
          return document.querySelector('.lease.lease--original');
        }

        const observer =
          typeof MutationObserver === 'function'
            ? new MutationObserver(scheduleRepaint)
            : null;

        function watchDocument() {
          const root = leaseRoot();
          if (!observer || !root) return;

          observer.observe(root, {
            subtree: true,
            childList: true,
            characterData: true,
          });
        }

        /**
         * Clicking a red underline opens what it is about.
         *
         * The ranges are not in the document — that is the whole point of
         * painting them this way — so there is no element to hang a listener
         * on. The click's caret position is tested against the ranges instead.
         *
         * On `click` rather than `mousedown`, so the editor has already placed
         * the caret and this is purely additive: the drafter is still putting
         * their cursor where they meant to.
         */
        function caretAt(event) {
          if (document.caretRangeFromPoint)
            return document.caretRangeFromPoint(event.clientX, event.clientY);

          if (document.caretPositionFromPoint) {
            const position = document.caretPositionFromPoint(
              event.clientX,
              event.clientY,
            );
            return position
              ? { startContainer: position.offsetNode, startOffset: position.offset }
              : null;
          }

          return null;
        }

        // What a click already means in this document.
        //
        // A lease-var takes you to its field in Deal Terms; the numbering and
        // reference controls have their own jobs. Those behaviours are the
        // document's and they win — a mark drawn over one of them must not
        // steal its click.
        //
        // This costs nothing a drafter needs. A blank inside a lease-var is a
        // deal term nobody has entered, and Deal Terms is exactly where that
        // gets fixed — so the click is already going to the right place, and
        // filling the field makes the finding disappear on the next pass. The
        // finding stays reachable from the list either way.
        const DOCUMENT_OWNS_THE_CLICK =
          '.transformed--lease-var, lease-var, crr, [data-list-static-item="true"]';

        function openFindingAt(event) {
          if (!panelOpen() || !supportsPreciseHighlights()) return;

          const target = event.target;
          if (
            target &&
            typeof target.closest === 'function' &&
            target.closest(DOCUMENT_OWNS_THE_CLICK)
          )
            return;

          const caret = caretAt(event);
          if (!caret) return;

          const id = findingAtPoint(caret.startContainer, caret.startOffset);
          if (!id) return;

          // From the GROUPED list, which is the one carrying each finding's
          // decision. `scope.findings` is the raw set — clicking an underline
          // opened a set-aside finding as though nobody had decided it, offered
          // Set aside again, and re-posted the decision on a second click.
          const finding = scope.groups
            .reduce((all, group) => all.concat(group.findings), [])
            .find(candidate => candidate.id === id);
          if (!finding || finding.id === (scope.selected && scope.selected.id))
            return;

          scope.$apply(function() {
            // Not `select`, which would also scroll — the drafter is already
            // looking at the text they clicked, and moving the page under them
            // is the opposite of helpful.
            scope.selected = finding;
            markSelected(finding);
          });
        }

        document.addEventListener('click', openFindingAt, true);

        // The send moment, from Download or from Share by Mail. Broadcast
        // rather than called, because both of those live in other directives
        // and neither should have to know this one exists.
        //
        // Synchronous on purpose: the download notice reads the published
        // summary immediately after firing this, so the mechanical pass has to
        // have run and published by the time it looks.
        const stopArming = $rootScope.$on('lp:document-sent', function() {
          arm();
          scope.run(false);
        });
        scope.$on('$destroy', function() {
          stopWatching();
          stopArming();
          document.removeEventListener('click', openFindingAt, true);
          if (observer) observer.disconnect();
          $timeout.cancel(repaintTimer);
          // The marks are the panel's, not the document's. Left behind when the
          // panel closes they are underlines with nothing to explain them.
          markDocument([]);
          markSelected(null);
        });

        // The pane is hidden rather than destroyed, so this directive is linked
        // for the whole session whether or not anybody opens it. That is what
        // makes the continuous half possible — and what makes it necessary to
        // hold the model call back until the panel is actually looked at.
        // Both conditions, whichever arrives last: the pane has to be open to
        // be worth paying for, and the lease has to have loaded for there to be
        // a document type to ask about.
        // The marks follow the panel. Watched separately from the read below,
        // because opening the panel has to repaint immediately whether or not
        // anything needs re-reading — and closing it has to clear.
        scope.$watch(
          function() {
            return scope.leftPane;
          },
          function() {
            markDocument(
              scope.groups.reduce((all, group) => all.concat(group.findings), []),
            );
            if (!panelOpen()) markSelected(null);
          },
        );

        scope.$watch(
          function() {
            return scope.leftPane === 'checks' && !!currentLease().type;
          },
          function(worthReading) {
            // Opening the panel is a question, and it deserves an answer
            // whatever state the document is in. The mechanical pass has been
            // running all along and holding its tongue, so arming it needs a
            // regroup to say what it already knows — the deep read below only
            // happens once, and would leave the list empty on every reopen.
            if (worthReading && !readyToReport()) {
              arm();
              scope.run(false);
            }
            if (worthReading && !scope.lastRunAt && !scope.reading)
              scope.run(true);
          },
        );

        $timeout(function() {
          scope.run(false);
        }, 0);
      },
    };
  },
]);
